import { useRef, useState } from 'react';

import Modal from '@ui/modal/modal';
import Container from '@ui/container/container';
import useAutoCreateAndPublishPreview from '@/utils/use-auto-create-and-publish-preview';
import {
  getWorkingProject,
  selectSettingUpPreview,
  selectUpdatingProjectPreviewPassword,
} from '@/store/slices/project/selectors';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import useGetPreviewUrl from '@/utils/use-get-preview-url';
import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import Button from '@ui/buttons/button/button';
import { updateProjectPreviewPassword } from '@/store/slices/project';

type CreatePreviewProjectProps = {
  open: boolean;
  handleClose: () => void;
};

const ValueWrapper = ({ value }: { value: string }) => {
  return (
    <Container
      borderWidth="1px"
      borderColor="var(--gray-400)"
      borderRadius="100px"
      minHeight="40px"
      height="40px"
      width="auto"
      className="padding-t-10 padding-b-12 padding-h-14 S2 text-ellipsis flex-1"
    >
      <Text fontSize={14} color="var(--gray-400)">
        {value}
      </Text>
    </Container>
  );
};

const CreatePreviewProject = ({ open, handleClose }: CreatePreviewProjectProps) => {
  const dispatch = useAppDispatch();

  const workingProject = useAppSelector(getWorkingProject);
  const settingUpPreview = useAppSelector(selectSettingUpPreview);
  const isUpdatingProjectPreviewPassword = useAppSelector(selectUpdatingProjectPreviewPassword);

  const [copiedContent, setCopiedContent] = useState({ copiedUrl: false, copiedPassword: false });

  const copyUrlTimeout = useRef<NodeJS.Timeout | null>(null);
  const copyPasswordTimeout = useRef<NodeJS.Timeout | null>(null);

  useAutoCreateAndPublishPreview({ workingProject, skipHook: !open });

  const previewUrl = useGetPreviewUrl(workingProject);
  const password = workingProject?.event_preview?.password;
  const projectPreviewUuid = workingProject?.event_preview?.uuid;

  const onClose = () => {
    if (settingUpPreview) return;
    handleClose();
  };

  const copyEventPreviewUrl = () => {
    if (!previewUrl) return;

    if (copyUrlTimeout.current) {
      clearTimeout(copyUrlTimeout.current);
    }

    navigator.clipboard.writeText(previewUrl);

    setCopiedContent((prev) => ({ ...prev, copiedUrl: true }));

    copyUrlTimeout.current = setTimeout(() => {
      setCopiedContent((prev) => ({ ...prev, copiedUrl: false }));
    }, 2000);
  };

  const copyPassword = () => {
    if (!password) return;

    if (copyPasswordTimeout.current) {
      clearTimeout(copyPasswordTimeout.current);
    }

    navigator.clipboard.writeText(password);

    setCopiedContent((prev) => ({ ...prev, copiedPassword: true }));

    copyPasswordTimeout.current = setTimeout(() => {
      setCopiedContent((prev) => ({ ...prev, copiedPassword: false }));
    }, 2000);
  };

  const changePasscode = () => {
    if (projectPreviewUuid) {
      dispatch(updateProjectPreviewPassword(projectPreviewUuid));
    }
  };

  return (
    <Modal
      open={open}
      onRequestClose={onClose}
      customSize={{
        width: '640px',
        height: 'auto',
        minHeight: '100px',
        maxHeight: 'calc(100% - 32px)',
        maxWidth: 'calc(100% - 32px)',
      }}
      showLoadingIndicator={settingUpPreview}
      RenderHeaderContent={() => {
        return (
          <Container borderRadius="12px 12px 0 0" className="flex jc-space-between" padding="24px 40px 24px 40px">
            <Text tag="h3" className="H3">
              Private link
            </Text>

            <Button className="clear-button padding-4" onClick={onClose}>
              <Icon name={ICONS.CLOSE} size={16} color="var(--gray-400)" />
            </Button>
          </Container>
        );
      }}
      RenderFooterContent={() => {
        return (
          <Container
            background="transparent"
            className="flex fd-row jc-flex-end ai-center padding-h-40 padding-v-24 gap-8"
          >
            <Button
              onClick={changePasscode}
              className="padding-v-16 padding-h-24"
              backgroundColor="var(--gray-600)"
              hoverBackgroundColor="var(--gray-500)"
              height="48px"
              disabled={isUpdatingProjectPreviewPassword}
            >
              <Text fontSize={'13px'}>Change passcode</Text>
            </Button>
          </Container>
        );
      }}
    >
      <div className="padding-h-40 padding-b-24 flex-column gap-8 height-100-percent width-100-percent">
        <Text className="margin-l-14 width-95 C1" color="var(--gray-400)">
          URL
        </Text>
        <div className="flex fd-row jc-flex-start ai-center width-100-percent">
          <ValueWrapper value={previewUrl} />
          <Button
            className="flex jc-flex-center ai-center radius-full square-40 padding-14 margin-l-4"
            onClick={copyEventPreviewUrl}
            backgroundColor="var(--gray-600)"
            hoverBackgroundColor="var(--gray-500)"
          >
            <Icon name={copiedContent.copiedUrl ? ICONS.CHECK : ICONS.COPY_FILLED} size={14} />
          </Button>
        </div>
        <Text className="margin-l-14 margin-t-8 width-95 C1" color="var(--gray-400)">
          Passcode
        </Text>
        <div className="flex fd-row jc-flex-start ai-center width-100-percent">
          {password && <ValueWrapper value={password} />}
          <Button
            className="flex jc-flex-center ai-center radius-full square-40 padding-14 margin-l-4"
            onClick={copyPassword}
            backgroundColor="var(--gray-600)"
            hoverBackgroundColor="var(--gray-500)"
          >
            <Icon name={copiedContent.copiedPassword ? ICONS.CHECK : ICONS.COPY_FILLED} size={14} />
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CreatePreviewProject;
